import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { api } from '../lib/api'
import { Calendar, CheckCircle, ListChecks, Play, ArrowRight, SkipForward } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import SuccessorMapGrid from './SuccessorMapGrid'
import TransitionPreviewAccordion from './TransitionPreviewAccordion'

interface SchoolYear {
  id: number
  name: string
  start_date: string
  end_date: string
}

interface TransitionSuggestion {
  student_id: number
  student_name: string
  from_unit_type: string
  from_unit_id: number
  from_unit_name: string
  to_unit_type: string
  to_unit_id: number
  to_unit_name: string
  current_membership_id: number
  notes?: string
}

interface RepeaterCandidateStudent {
  student_id: number
  student_name: string
  current_membership_id: number
}

interface RepeaterCandidateGroup {
  from_unit_type: string
  from_unit_id: number
  from_unit_name: string
  students: RepeaterCandidateStudent[]
}

interface TransitionGroup {
  from_unit_type: string
  from_unit_id: number
  from_unit_name: string
  to_unit_type: string
  to_unit_id: number
  to_unit_name: string
  transitions: TransitionSuggestion[]
  repeater_count: number
}

const steps = [
  { id: 0, title: 'Start', icon: Calendar },
  { id: 1, title: 'Klassen', icon: ListChecks },
  { id: 2, title: 'Kurse', icon: SkipForward },
  { id: 3, title: 'Vorschau', icon: CheckCircle },
  { id: 4, title: 'Ausführen', icon: Play }
]

function formatDate(value?: string) {
  if (!value) return '-'
  const date = new Date(`${value}T12:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('de-DE')
}

export default function SchoolYearTransitionWizard() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isAdmin = user?.role === 'administrator' || user?.role === 'admin'

  const [step, setStep] = useState(0)
  const [transitionDate, setTransitionDate] = useState('')
  const [selectedSchoolYearId, setSelectedSchoolYearId] = useState<number | ''>('')
  const [selectedTransitionIds, setSelectedTransitionIds] = useState<Set<number>>(new Set())
  const [selectedRepeaterIds, setSelectedRepeaterIds] = useState<Set<number>>(new Set())
  const [executeResult, setExecuteResult] = useState<{ created: number; ended: number; repeatersProcessed?: number; errors: unknown[] } | null>(null)
  const [coursesSkipped, setCoursesSkipped] = useState(false)
  const [classesReady, setClassesReady] = useState(false)

  const { data: currentPeriodResponse } = useQuery(
    ['school-years', 'current'],
    () => api.get('/school-years/current').then(res => res.data),
    { staleTime: 60_000 }
  )
  const currentPeriod = currentPeriodResponse?.data

  const { data: schoolYearsResponse } = useQuery(
    ['school-years'],
    () => api.get('/school-years').then(res => res.data),
    { staleTime: 60_000 }
  )
  const schoolYears: SchoolYear[] = schoolYearsResponse?.data || []

  const { data: switchSuggestionResponse } = useQuery(
    ['school-years', 'switch-suggestion'],
    () => api.get('/school-years/switch-suggestion').then(res => res.data),
    { staleTime: 60_000, enabled: isAdmin }
  )
  const switchSuggestion = switchSuggestionResponse?.data

  const { data: transitionTargetResponse } = useQuery(
    ['school-years', 'transition-target'],
    () => api.get('/school-years/transition-target').then(res => res.data),
    { staleTime: 60_000 }
  )
  const transitionTarget = transitionTargetResponse?.data

  const { data: batchResponse, isLoading: batchLoading } = useQuery(
    ['transitions-suggestions-batch', transitionDate],
    () => api.get('/transitions/suggestions-batch', { params: { transition_date: transitionDate } }).then(res => res.data),
    { enabled: step >= 3 && !!transitionDate }
  )
  const batchSuggestions: TransitionSuggestion[] = batchResponse?.data?.suggestions || []
  const repeaterCandidates: RepeaterCandidateGroup[] = batchResponse?.data?.repeater_candidates || []
  const groupedSuggestions: TransitionGroup[] = batchResponse?.data?.groups || []

  useEffect(() => {
    if (!transitionTarget?.transition_date) return
    setTransitionDate(transitionTarget.transition_date)
    if (transitionTarget.schoolYear?.id != null) {
      setSelectedSchoolYearId(transitionTarget.schoolYear.id)
    }
  }, [transitionTarget?.transition_date, transitionTarget?.schoolYear?.id])

  useEffect(() => {
    if (step !== 3 || batchSuggestions.length === 0) return
    setSelectedTransitionIds(new Set(batchSuggestions.map(s => s.current_membership_id)))
    setSelectedRepeaterIds(new Set())
  }, [step, batchSuggestions])

  const selectedTransitionsForExecute = useMemo(
    () =>
      batchSuggestions
        .filter(s => selectedTransitionIds.has(s.current_membership_id))
        .map(s => ({
          current_membership_id: s.current_membership_id,
          student_id: s.student_id,
          from_unit_type: s.from_unit_type,
          from_unit_id: s.from_unit_id,
          to_unit_type: s.to_unit_type,
          to_unit_id: s.to_unit_id,
          notes: s.notes
        })),
    [batchSuggestions, selectedTransitionIds]
  )

  const selectedRepeatersForExecute = useMemo(
    () =>
      repeaterCandidates.flatMap(group =>
        group.students
          .filter(student => selectedRepeaterIds.has(student.current_membership_id))
          .map(student => ({
            student_id: student.student_id,
            unit_type: group.from_unit_type,
            unit_id: group.from_unit_id,
            current_membership_id: student.current_membership_id
          }))
      ),
    [repeaterCandidates, selectedRepeaterIds]
  )

  const executeBatchMutation = useMutation(
    (payload: any) => api.post('/transitions/execute-batch', payload).then(res => res.data),
    {
      onSuccess: (data: any) => {
        setExecuteResult(data?.data || null)
        queryClient.invalidateQueries(['class'])
        queryClient.invalidateQueries(['course'])
        queryClient.invalidateQueries(['group'])
        queryClient.invalidateQueries(['memberships'])
        toast.success('Schuljahreswechsel erfolgreich durchgeführt')
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.error || 'Schuljahreswechsel fehlgeschlagen')
      }
    }
  )

  const canOpenStep = (targetStep: number) => {
    if (targetStep <= 0) return true
    if (targetStep === 1) return Boolean(transitionDate)
    if (targetStep === 2) return Boolean(transitionDate) && classesReady
    if (targetStep >= 3) return Boolean(transitionDate) && classesReady
    return false
  }

  const toggleTransition = (membershipId: number) => {
    setSelectedTransitionIds(prev => {
      const next = new Set(prev)
      if (next.has(membershipId)) next.delete(membershipId)
      else next.add(membershipId)
      return next
    })
  }

  const toggleRepeater = (membershipId: number) => {
    setSelectedRepeaterIds(prev => {
      const next = new Set(prev)
      if (next.has(membershipId)) next.delete(membershipId)
      else next.add(membershipId)
      return next
    })
  }

  const toggleRepeaterGroup = (group: RepeaterCandidateGroup) => {
    const allSelected = group.students.every(student => selectedRepeaterIds.has(student.current_membership_id))
    setSelectedRepeaterIds(prev => {
      const next = new Set(prev)
      group.students.forEach(student => {
        if (allSelected) next.delete(student.current_membership_id)
        else next.add(student.current_membership_id)
      })
      return next
    })
  }

  return (
    <div className="card p-4 sm:p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Schuljahreswechsel</h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Klassen zuerst: Folgeklassen prüfen, dann Kurse, anschließend zentral ausführen.
      </p>

      <nav className="mt-6" aria-label="Fortschritt">
        <ol className="flex flex-wrap gap-2 sm:gap-3">
          {steps.map((entry) => {
            const Icon = entry.icon
            const active = step === entry.id
            const disabled = !canOpenStep(entry.id)
            return (
              <li key={entry.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => setStep(entry.id)}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
                    active
                      ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-200 dark:bg-primary-950/40 dark:text-primary-300 dark:ring-primary-800'
                      : disabled
                        ? 'cursor-not-allowed text-gray-400'
                        : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {entry.title}
                </button>
              </li>
            )
          })}
        </ol>
      </nav>

      <div className="mt-6 space-y-5">
        {step === 0 && (
          <>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/40">
              <p className="text-sm text-blue-900 dark:text-blue-200">
                Jetzt: <strong>{currentPeriod?.name || '-'}</strong>
              </p>
              <p className="mt-1 text-sm text-blue-800 dark:text-blue-300">
                Als Nächstes: <strong>{transitionTarget?.periodName || '-'}</strong> ab <strong>{formatDate(transitionDate)}</strong>
              </p>
              {switchSuggestion?.reason && (
                <p className="mt-2 text-xs text-blue-700 dark:text-blue-300">{switchSuggestion.reason}</p>
              )}
            </div>
            <button type="button" className="btn btn-primary" onClick={() => setStep(1)} disabled={!transitionDate}>
              Weiter zu den Klassen
            </button>
          </>
        )}

        {step === 1 && (
          <>
            <SuccessorMapGrid
              unitType="class"
              title="Folgeklassen"
              description="Jede Klasse braucht eine Folgeklasse, bevor der Wechsel startet."
              required
              onReadinessChange={setClassesReady}
            />
            <div className="flex gap-3">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(0)}>Zurück</button>
              <button type="button" className="btn btn-primary" onClick={() => setStep(2)} disabled={!classesReady}>
                Weiter zu den Kursen
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              Direkte Kurs-Mitgliedschaften werden verschoben. Schülerinnen und Schüler aus Klassen-Pools wechseln hier nicht automatisch mit.
            </div>
            <SuccessorMapGrid
              unitType="course"
              title="Folgekurse"
              description="Optional: Folgekurse prüfen oder überspringen."
              includeMemberBreakdown
              skipped={coursesSkipped}
              onSkipChange={setCoursesSkipped}
            />
            <SuccessorMapGrid
              unitType="group"
              title="Folgegruppen"
              description="Optional: Gruppen mit Folgegruppen verknüpfen."
              skipped={coursesSkipped}
            />
            <div className="flex gap-3">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>Zurück</button>
              <button type="button" className="btn btn-primary" onClick={() => setStep(3)}>
                Zur Vorschau
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="sticky top-0 z-10 rounded-lg border border-gray-200 bg-white/95 p-3 text-sm shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-900/95">
              <strong>{selectedTransitionIds.size}</strong> Übergänge · <strong>{selectedRepeaterIds.size}</strong> Bleiben in gleicher Einheit · Gültig ab <strong>{formatDate(transitionDate)}</strong>
            </div>
            {batchLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">Vorschau wird geladen...</p>
            ) : (
              <TransitionPreviewAccordion
                groups={groupedSuggestions}
                repeaterCandidates={repeaterCandidates}
                selectedTransitionIds={selectedTransitionIds}
                selectedRepeaterIds={selectedRepeaterIds}
                onToggleTransition={toggleTransition}
                onToggleRepeater={toggleRepeater}
                onToggleRepeaterGroup={toggleRepeaterGroup}
                onSelectAllTransitions={() => setSelectedTransitionIds(new Set(batchSuggestions.map(s => s.current_membership_id)))}
                onDeselectAllTransitions={() => setSelectedTransitionIds(new Set())}
              />
            )}
            <div className="flex gap-3">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(2)}>Zurück</button>
              <button type="button" className="btn btn-primary" onClick={() => { setExecuteResult(null); setStep(4) }}>
                Zur Ausführung
              </button>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
              <p>Gültig ab: <strong>{formatDate(transitionDate)}</strong></p>
              <p className="mt-1">Übergänge: <strong>{selectedTransitionsForExecute.length}</strong></p>
              <p className="mt-1">Bleiben in gleicher Einheit: <strong>{selectedRepeatersForExecute.length}</strong></p>
            </div>
            {executeResult ? (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 dark:border-green-800 dark:bg-green-950/40 dark:text-green-200">
                Beendet: {executeResult.ended} · Neu: {executeResult.created}
                {(executeResult.repeatersProcessed || 0) > 0 && <> · Wiederholer: {executeResult.repeatersProcessed}</>}
                <div className="mt-3">
                  <button type="button" className="btn btn-secondary" onClick={() => setStep(0)}>
                    Neuer Durchlauf
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3 sm:flex-row">
                <button type="button" className="btn btn-secondary" onClick={() => setStep(3)}>
                  <ArrowRight className="h-4 w-4 rotate-180" />
                  Zurück zur Vorschau
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={(selectedTransitionsForExecute.length === 0 && selectedRepeatersForExecute.length === 0) || executeBatchMutation.isLoading}
                  onClick={() =>
                    executeBatchMutation.mutate({
                      transition_date: transitionDate,
                      transitions: selectedTransitionsForExecute,
                      repeaters: selectedRepeatersForExecute.length > 0 ? selectedRepeatersForExecute : undefined,
                      school_year_id: selectedSchoolYearId || undefined
                    })
                  }
                >
                  {executeBatchMutation.isLoading ? 'Wird ausgeführt...' : 'Jetzt ausführen'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
